import React, { useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { swSettings } from "app/common/services/swSettings";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { getExcelButtonText } from "app/components/React/ExcelButton/getExcelButtonText";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { i18nFilter } from "app/filters/i18nFilter";
import { GeographyTable } from "app/pages/website-analysis/geography/GeographyTable";
import GeoMarketShareMap from "app/pages/website-analysis/geography/GeoMarketShareMap";
import { useGeographyData } from "app/pages/website-analysis/geography/hooks/useGeographyData";
import RegionalsToggleBanner from "app/pages/website-analysis/geography/RegionalsToggleBanner";
import GeographyApiService from "app/pages/website-analysis/geography/services/GeographyApiService";
import {
    BoxContainer,
    StyledHeaderTitle,
    TitleContainer,
    WebsiteAnalysisGeoContainer,
    ButtonsContainer,
} from "app/pages/website-analysis/geography/StyledComponents";
import StyledCountriesPromoBanner from "app/pages/website-analysis/geography/StyledCountriesPromoBanner";
import type { IWebAnalysisGeoExcelRequestParams } from "app/pages/website-analysis/geography/types/GeoDataTypes";
import DurationService from "app/services/DurationService";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import { isSI, isTraditionalTrialOrAfterTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

export const WebAnalysisGeographyContainer = () => {
    const params = proNavigator.getParams() as any;

    const state = proNavigator.getState("digitalsuite_website_geography");

    const configId = state?.configId || "WebGeography";
    const apiParams = proNavigator.getApiParams(params, configId as string);

    const { webSource, includeSubDomains = params.isWWW === "*" } = apiParams;
    // Use React Query's queryClient
    const queryClient = useQueryClient();
    const flagKey = ["isRegionalsEnabled"]; // The key for React Query cache

    // Get the flag from the query cache, default to false if not found
    const [isRegionalEnabled, setIsRegionalEnabled] = useState(queryClient.getQueryData<boolean>(flagKey) ?? false);

    const { geographyApiService } = React.useMemo(
        () => ({
            geographyApiService: new GeographyApiService(),
        }),
        [],
    );
    const { data, isLoading, refetch } = useGeographyData({ ...apiParams, includeSubDomains }, isRegionalEnabled);
    useEffect(() => {
        refetch();
    }, [isRegionalEnabled]);

    const webAnalysisGeoTableData = data;
    const isGeoLoading = isLoading;
    const isCountriesBannerVisible = isTraditionalTrialOrAfterTrialUser(swSettings) && !isSI(swSettings);
    const isPaidExcelPermitted = isPaidExcelAllowed();
    // Toggle regionals handler
    const handleToggle = () => {
        allTrackers.dispatchTrackEvent("toggle", "click", "Website Analysis Geography", {
            isRegionalEnabled: !isRegionalEnabled,
        });
        const newFlagValue = !isRegionalEnabled;
        setIsRegionalEnabled(newFlagValue);
        // Update the flag value in React Query cache
        queryClient.setQueryData(flagKey, newFlagValue);
    };
    const renderNoData = () => {
        return (
            <TableNoData
                icon="no-data"
                messageTitle={i18nFilter()("global.nodata.notavilable")}
            />
        );
    };

    const durationObject = React.useMemo(() => DurationService.getDurationData(params.duration), [params.duration]);

    const tableExcelLink = useMemo(() => {
        const {
            from,
            to,
            timeGranularity = "Monthly",
            webSource = "Desktop",
            isWindow,
            orderBy,
            country,
            key,
        } = apiParams;
        return geographyApiService.getDomainsGeographyTableExcelUrl({
            from,
            to,
            timeGranularity,
            webSource,
            includeSubDomains,
            orderBy,
            country,
            keys: key,
            fileName: `Website Analysis Geography-(${key})-(from: ${from})-(to: ${to})`,
            isWindow,
        } as IWebAnalysisGeoExcelRequestParams);
    }, [params]);

    const isCompare = React.useMemo(() => {
        return params?.key?.split(",").length > 1;
    }, [params.key]);
    const subtitleFilters = React.useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: durationObject.raw.from.valueOf(),
                    to: durationObject.raw.to.valueOf(),
                    useRangeDisplay:
                        params.duration !== "28d" ||
                        durationObject.raw.from.format("YYYY-MM") !== durationObject.raw.to.format("YYYY-MM"),
                },
            },
            {
                filter: "webSource",
                value: params.webSource,
            },
        ],
        [params.country, durationObject],
    );

    const onExcelClick = () => {
        allTrackers.dispatchTrackEvent(
            "website.analysis.geography.excel.download",
            "submit-ok",
            "website.analysis.geography.excel.download",
        );
    };
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                {tableExcelLink ? (
                    <DownloadButtonMenu
                        PaidExcel={isPaidExcelPermitted}
                        totalCount={webAnalysisGeoTableData?.TotalCount}
                        Excel={true}
                        iconNameProps={!downloadExcelPermitted ? "excel-download" : null}
                        downloadUrl={tableExcelLink}
                        exportFunction={onExcelClick}
                        excelLocked={!downloadExcelPermitted}
                        buttonText={getExcelButtonText()}
                    />
                ) : null}
                <div>
                    <AddToDashboard
                        modelType="fromWebsite"
                        webSource={webSource}
                        type="Table"
                        metric={"WebsitesGeography"}
                        overrideAddToDashboardParams={{ country: "999" }}
                    />
                </div>
            </ButtonsContainer>
        );
    };

    return (
        <>
            {isCountriesBannerVisible ? <StyledCountriesPromoBanner /> : null}
            <WebsiteAnalysisGeoContainer>
                <BoxContainer data-automation-website-analysis-geo={true}>
                    <TitleContainer>
                        <FlexColumn>
                            <StyledHeaderTitle>
                                <BoxTitle tooltip={i18nFilter()("website.analysis.geography.title.tooltip")}>
                                    {i18nFilter()("segment.analysis.geo.title")}
                                </BoxTitle>
                            </StyledHeaderTitle>
                            <StyledBoxSubtitle>
                                <BoxSubtitle filters={subtitleFilters} />
                            </StyledBoxSubtitle>
                        </FlexColumn>
                        {!isRegionalEnabled ? getUtilityButtons() : null}
                    </TitleContainer>
                    {!isGeoLoading && !webAnalysisGeoTableData ? renderNoData() : null}
                    <RegionalsToggleBanner
                        isRegionalEnabled={isRegionalEnabled}
                        toggleRegionals={handleToggle}
                    ></RegionalsToggleBanner>
                    <GeoMarketShareMap
                        isCompare={isCompare}
                        isLoading={isGeoLoading}
                        isRegionalEnabled={isRegionalEnabled}
                        countriesTrafficShare={webAnalysisGeoTableData}
                    ></GeoMarketShareMap>
                    <GeographyTable
                        isCompare={isCompare}
                        tableData={webAnalysisGeoTableData}
                        isLoading={isGeoLoading}
                        isRegionalsEnabled={isRegionalEnabled}
                    />
                </BoxContainer>
            </WebsiteAnalysisGeoContainer>
        </>
    );
};
